import axios from 'axios';
import { SERVER } from '../../Constants';

const apiClient = axios.create({
    baseURL: SERVER + '/legal',
    withCredentials: true
});

/**
 * Fetches all legal documents (manager only)
 * @returns {Promise<Object>} Response data containing legal documents array
 * @throws {Error} If request fails
 */
export const getLegals = async () => {
    try {
        const res = await apiClient.get('/');
        return res.data;
    } catch (error) {
        console.error('getLegals error:', error.response);
        throw new Error(error.response?.data?.message || 'Failed to fetch legal documents');
    }
};

/**
 * Fetches a specific legal document by its label
 * @param {string} label - The document label ('termsAndConditions', 'privacyPolicy', or 'cookiePolicy')
 * @returns {Promise<Object>} Response data containing the requested legal document
 * @throws {Error} If request fails or document not found
 */
export const getLegalByLabel = async (label) => {
    try {
        const res = await apiClient.get(`/${label}`);
        return res.data;
    } catch (error) {
        console.error('getLegalByLabel error:', error.response);
        throw new Error(error.response?.data?.message || `Failed to fetch ${label} document`);
    }
};

/**
 * Creates a new legal document (manager only)
 * @param {Object} documentData - The document data to create
 * @param {string} documentData.label - Document label ('termsAndConditions', 'privacyPolicy', or 'cookiePolicy')
 * @param {string} documentData.title - Document title
 * @param {Object|string} documentData.content - Document content (TipTap JSON or HTML string)
 * @returns {Promise<Object>} Response data containing the created document
 * @throws {Error} If request fails or validation error occurs
 */
export const createLegalDocument = async (documentData) => {
    try {
        const res = await apiClient.post('/', documentData);
        return res.data;
    } catch (error) {
        console.error('createLegalDocument error:', error.response);
        throw new Error(error.response?.data?.message || 'Failed to create legal document');
    }
};

/**
 * Updates an existing legal document (manager only)
 * @param {string} label - The document label to update
 * @param {Object} updateData - The document data to update
 * @param {string} [updateData.title] - New document title (optional)
 * @param {Object|string} [updateData.content] - New document content (optional)
 * @returns {Promise<Object>} Response data containing the updated document
 * @throws {Error} If request fails or document not found
 */
export const updateLegalDocument = async (label, updateData) => {
    try {
        const res = await apiClient.put(`/${label}`, updateData);
        return res.data;
    } catch (error) {
        console.error('updateLegalDocument error:', error.response);
        throw new Error(error.response?.data?.message || `Failed to update ${label} document`);
    }
};

/**
 * Deletes a legal document (manager only)
 * @param {string} label - The document label to delete
 * @returns {Promise<Object>} Response data with success message
 * @throws {Error} If request fails or document not found
 */
export const deleteLegalDocument = async (label) => {
    try {
        const res = await apiClient.delete(`/${label}`);
        return res.data;
    } catch (error) {
        console.error('deleteLegalDocument error:', error.response);
        throw new Error(error.response?.data?.message || `Failed to delete ${label} document`);
    }
};

/**
 * Initializes default legal documents (manager only)
 * Creates default terms, privacy policy, and cookie policy if they don't exist
 * @returns {Promise<Object>} Response data with success message
 * @throws {Error} If request fails
 */
export const initializeLegalDocuments = async () => {
    try {
        const res = await apiClient.post('/init');
        return res.data;
    } catch (error) {
        console.error('initializeLegalDocuments error:', error.response);
        throw new Error(error.response?.data?.message || 'Failed to initialize legal documents');
    }
};

/**
 * Fetches terms and conditions document
 * @returns {Promise<Object>} Response data containing terms and conditions
 * @throws {Error} If request fails or document not found
 */
export const getTermsAndConditions = async () => {
    return getLegalByLabel('termsAndConditions');
};

/**
 * Fetches privacy policy document
 * @returns {Promise<Object>} Response data containing privacy policy
 * @throws {Error} If request fails or document not found
 */
export const getPrivacyPolicy = async () => {
    return getLegalByLabel('privacyPolicy');
};

/**
 * Fetches cookie policy document
 * @returns {Promise<Object>} Response data containing cookie policy
 * @throws {Error} If request fails or document not found
 */
export const getCookiePolicy = async () => {
    return getLegalByLabel('cookiePolicy');
};

export default {
    getLegals,
    getLegalByLabel,
    createLegalDocument,
    updateLegalDocument,
    deleteLegalDocument,
    initializeLegalDocuments,
    getTermsAndConditions,
    getPrivacyPolicy,
    getCookiePolicy
};